/*
* Association analysis report view for authenticated users
*/

import 'hammerjs';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import scrollToComponent from 'react-scroll-to-component';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import Branding from '../../branding/branding';
import * as AppComp from '../../common/app-component-base';
import * as AppUtilities from '../../common/apputilities';
import { BootstrapColXl as Col } from '../../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../../common/formatters';
import { GridUtilities } from '../../common/grid/gridutilities';
import { NavigationLinks } from '../../common/navigation';
import ApiEndpoints from '../../config/apiendpoints';
import { AppImages } from '../../config/images';
import { isLevel3Report } from '../../order/utils';
import * as ReduxQueries from '../../queries';
import * as ReportCharts from '../charts';
import * as ReportLogic from '../logic';
import * as ReportCommon from './../report-common';
import { normalizeSpecialAssessments } from '../special-assessments';
import { getSpecialAssessmentPresentation, SPECIAL_ASSESSMENT_SCHEDULE_ROWS_PER_PAGE } from './special-assessments-presentation';
import * as ReportComponents from './report-components';

import { Render as ReportPageComponentEvaluation } from './pages/report-page-component-evaluation';
import { Render as ReportPageComponentFunding } from './pages/report-page-component-funding';
import { Render as ReportPageCover } from './pages/report-page-cover';
import { Render as ReportPageExecutiveSummary } from './pages/report-page-executive-summary';
import { Render as ReportPageFaq } from './pages/report-page-faq';
import { Render as ReportPageFundingSummary } from './pages/report-page-funding-summary';
import { Render as ReportPageGlossary } from './pages/report-page-glossary';
import { Render as ReportPageIntroduction } from './pages/report-page-introduction';
import { Render as ReportPagePercentFundedGraphKendo } from './pages/report-page-percent-funded-graph-kendo';
import { Render as ReportPageProjectedReserveExpenditures } from './pages/report-page-projected-reserve-expenditures';
import { Render as ReportPageReserveContributionsGraphKendo } from './pages/report-page-reserve-contributions-graph-kendo';
import { Render as ReportPageSignificantComponents } from './pages/report-page-significant-components';
import { Render as ReportPageSignificantComponentsGraphKendo } from './pages/report-page-significant-components-graph-kendo';
import { Render as ReportPageSpecialAssessments } from './pages/report-page-special-assessments';
import { Render as ReportPageTOC } from './pages/report-page-table-of-contents';
import { Render as ReportPageYearlyCashFlow } from './pages/report-page-yearly-cash-flow';
import { Render as ReportPageYearlyReserveExpendituresGraphKendo } from './pages/report-page-yearly-reserve-expenditures-graph-kendo';
import { Render as ReportPageYearlySummary } from './pages/report-page-yearly-summary';

import { isNullOrUndefined } from '../../../src/utils/generalUtils';
import '../../branding/report-pdf.css';
import '../pdf.css';

export interface IAssociationReportPdfProps extends AppComp.IAppComponentProps {
    association?: any;
    associationId?: any;
    currentOrder?: any;
    currentOrderChart?: any;
    inventory?: any;
    scenarios?: any;
    userResumes?: any;
    navigateTo?: any;
}

export interface IAssociationReportPdfState extends AppComp.IAppComponentState {
    formChanges?: any;
    inventoryChanges?: any;
    reportPageCount: number;
}

class AssociationReportPdf extends AppComp.AppComponent<IAssociationReportPdfProps, IAssociationReportPdfState> {
    public static readonly scrollToConfig = {
        align: 'top',
        ease: 'out-circ',
        duration: 550,
        offset: -55
    };

    public originalExtendedInventoryCache: Array<any> = [];
    public sectionPages: Array<any> = [];
    public pdfExportComponent: any = undefined;

    public constructor(props: any) {
        super(props, 'app.report-association.');

        this.state = {
            formChanges: {},
            inventoryChanges: [],
            reportPageCount: 0,
        };

        ReportCharts.setHighchartOptions();
    }

    private scheduleLayoutKey: number | undefined = undefined;

    public extendCurrentOrderUsers(currentOrder: any, userResumes: Array<any>) {
        const managerObjId = _.get(currentOrder, 'user.userObjectId', '');
        const manager = _.find(userResumes, ['user.userObjectId', managerObjId]);

        const siteInspectorObjId = _.get(currentOrder, 'siteInspector.userObjectId', '');
        const siteInspector = _.find(userResumes, ['user.userObjectId', siteInspectorObjId]);

        return {
            manager: manager ? _.cloneDeep(manager) : {},
            siteInspector: siteInspector ? _.cloneDeep(siteInspector) : {},
        };
    }

    public extendAssociationData(record: any) {
        if (!record) { return; }

        let result = _.cloneDeep(record);
        const invSubgroups = AppUtilities.getArray(record, 'inventorySubgroups');
        let filteredSubgroups = _.filter(invSubgroups, invSubgroup => invSubgroup.groupCode !== 'XYZ');
        if (AppUtilities.isArrayWithLength(filteredSubgroups)) {
            // result.inventorySubgroups.push({ groupCode: '', title: '', label: this.appText('filter-option-any') });
            result.inventorySubgroups = filteredSubgroups.map(c => {
                if (!c.label) {
                    c.label = Formatters.composeInventorySubgroupLabel(c, true); // c.label = `${c.groupCode} - ${c.title}`;
                }

                return c;
            });
        }

        return result;
    }

    public extendInventoryData(inventory: Array<any>, inventoryChanges: Array<any>, association: any) {
        // Extend grid row data
        if (!AppUtilities.isArrayWithLength(inventory)) { return []; }

        let filteredInventory = _.filter(inventory, invItem => invItem.subgroup !== 'XYZ');
        const extended = filteredInventory.map((r, i) => {
            let record = _.cloneDeep(r);

            record.avgCost = ReportLogic.calculateInventoryItemAvgCost(record);
            record.avgCost_sort = Number(record.avgCost);
            if (!record.avgCost) {
                GridUtilities.recordSetRowClass(record, 'row-no-avg-cost');
                record.usefulLife = 'N/A';
                record.remainingUsefulLife = 0;
            }

            const invSubgroups = AppUtilities.getArray(association, 'inventorySubgroups');
            let filteredSubgroups = _.filter(invSubgroups, invSubgroup => invSubgroup.groupCode !== 'XYZ');
            record.Group = '';
            if (AppUtilities.isArrayWithLength(filteredSubgroups)) {
                let subgroupDef: any = _.find(filteredSubgroups, ['groupCode', _.get(record, 'subgroup', '')]);
                if (subgroupDef && subgroupDef.title) { record.Group = subgroupDef.title; }
            }

            record.usefulLife_sort = (AppUtilities.isNumeric(record.usefulLife)) ? Formatters.int(record.usefulLife) : 0;
            record.remainingUsefulLife_sort = (AppUtilities.isNumeric(record.remainingUsefulLife)) ? Formatters.int(record.remainingUsefulLife) : 0;

            record.included = _.get(record, 'included', (record.avgCost_sort !== 0 && record.usefulLife !== 'N/A') ? true : false);
            record.hypAvgCost = Formatters.float(_.get(record, 'hypAvgCost', record.avgCost_sort), 2);
            record.hypUsefulLife = Formatters.int(_.get(record, 'hypUsefulLife', record.usefulLife_sort));
            record.hypRemainingUsefulLife = Formatters.int(_.get(record, 'hypRemainingUsefulLife', record.remainingUsefulLife_sort));

            let invChanges = _.find(inventoryChanges, { inventoryId: record.inventoryId });
            if (invChanges) {
                record = _.merge(record, _.omit(invChanges, 'inventoryId'));
            }

            record.editing = record.included;

            return record;
        });

        if (this.originalExtendedInventoryCache.length === 0) { this.originalExtendedInventoryCache = _.cloneDeep(extended); }

        return extended;
    }

    public getFormData(currentOrderChart: any, currentOrder: any) {
        const result = {
            calculateYears: ReportCommon.CalculateYears, // Formatters.getInt(currentOrderChart, 'years', ReportCommon.CalculateYears),

            currentAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentAnnualIncrease', 5, 1, 0.03),
            currentMRCVal: Formatters.getFloat(currentOrderChart, 'currentMRCVal', 2),
            currentSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'currentSecondaryAnnualIncrease', 5, 1, 0.03),
            currentYears: Formatters.getInt(currentOrderChart, 'currentYears', ReportCommon.CalculateYears),

            minimumAnnualIncrease: Formatters.getFloat(currentOrderChart, 'minimumAnnualIncrease', 5, 1, 0.03),
            minimumMRCVal: Formatters.getFloat(currentOrderChart, 'minimumMRCVal', 2),
            minimumSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'minimumSecondaryAnnualIncrease', 5, 1, 0.03),
            minimumYears: Formatters.getInt(currentOrderChart, 'minimumYears', ReportCommon.CalculateYears),

            recommendedAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedAnnualIncrease', 5, 1, 0.03),
            recommendedMRCVal: Formatters.getFloat(currentOrderChart, 'recommendedMRCVal', 2),
            recommendedSecondaryAnnualIncrease: Formatters.getFloat(currentOrderChart, 'recommendedSecondaryAnnualIncrease', 5, 1, 0.03),
            recommendedYears: Formatters.getInt(currentOrderChart, 'recommendedYears', ReportCommon.CalculateYears),

            // aka idealBalance
            currentFFB: Formatters.getFloat(currentOrderChart, 'ffb', 2) ? Formatters.getFloat(currentOrderChart, 'ffb', 2) : Formatters.getFloat(currentOrderChart, 'begBalance', 2),

            draft: _.get(currentOrderChart, 'draft', true) ? true : false,
            showMinimum: _.get(currentOrderChart, 'showMinimum', false) ? true : false,
            showRecommended: _.get(currentOrderChart, 'showRecommended', false) ? true : false,
            showYearlySummaryCurrent: _.get(currentOrderChart, 'showYearlySummaryCurrent', false) ? true : false,
            showYearlySummaryMinimum: _.get(currentOrderChart, 'showYearlySummaryMinimum', false) ? true : false,
            showYearlySummaryRecommended: _.get(currentOrderChart, 'showYearlySummaryRecommended', false) ? true : false,
            showCurrent: _.get(currentOrderChart, 'showCurrent', false) ? true : false,
            showPageNumbers: _.get(currentOrderChart, 'showPageNumbers', false) ? true : false,
            groupComponents: _.get(currentOrderChart, 'groupComponents', false) ? true : false,
            groupComponentsPictureSection: _.get(currentOrderChart, 'groupComponentsPictureSection', false) ? true : false,
            hideFooter: _.get(currentOrderChart, 'hideFooter', false) ? true : false,
            hideComponentEvaluation: _.get(currentOrderChart, 'hideComponentEvaluation', false) ? true : false,
            hideNumberOfUnits: _.get(currentOrderChart, 'hideNumberOfUnits', false) ? true : false,

            inflation: Formatters.getFloat(currentOrderChart, 'inflation', 5, 1), // 100),
            interest: Formatters.getFloat(currentOrderChart, 'interest', 5, 1), // 100),
            perFirstYear: Formatters.getInt(currentOrderChart, 'perFirstYear', 12),

            begBalance: Formatters.getFloat(currentOrderChart, 'begBalance', 2),
            beginYear: parseInt(_.get(currentOrderChart, 'beginYear', new Date().getFullYear()), 10),
            calculateYearsShort: 29, // parseInt(_.get(currentOrderChart, 'years', ReportCommon.CalculateYears), 10) - 1,
            mrcValue: Number((_.get(currentOrderChart, 'recommendedMRCVal', _.get(currentOrder, 'recommendedMRC', 0))).toFixed(2)),
            specialAssessment: Number((_.get(currentOrderChart, 'sa', 0)).toFixed(2)),
            specialAssessmentsChart: _.get(currentOrderChart, 'specialAssessments', []),
            years: Number((_.get(currentOrderChart, 'recommendedYears', 0)).toFixed(0)),
        };

        return _.merge(result, this.state.formChanges);
    }

    public handleAdjustmentFormInputChange(n: any, v: any) {
        let changes = Object.assign({}, this.state.formChanges);
        changes[n] = Number(v);

        this.setState({ formChanges: changes });
    }

    public handleItemChange(event: any) {
        const itemField = event.field;
        const itemValueNew = event.value;
        const itemValueOld = event.dataItem[itemField];
        const itemId = event.dataItem.inventoryId;
        const origItem = _.find(this.originalExtendedInventoryCache, { 'inventoryId': itemId });
        const origValue = _.get(origItem, itemField);

        let inv = _.cloneDeep(this.state.inventoryChanges);
        if (!_.find(inv, { 'inventoryId': itemId })) {
            const newItem = { inventoryId: itemId, };
            inv.push(newItem);
        }

        let item = _.find(inv, { 'inventoryId': itemId });
        if (item && _.get(item, itemField) !== itemValueNew) {
            if (itemValueNew === origValue) {
                // New value same as original, delete any change
                _.unset(item, itemField);
            } else {
                // New value differs from original
                _.set(item, itemField, itemValueNew);
            }

            if (_.keys(_.omit(item, 'inventoryId')).length === 0) {
                // No more changes for this item, remove it
                inv = _.reject(inv, { inventoryId: itemId });
            }

            _.set(event.dataItem, itemField, itemValueNew);

            this.setState({ inventoryChanges: inv });
        }
    }

    public getIsHypothetical() {
        const isHypothetical = (this.state.inventoryChanges.length === 0 && _.keys(this.state.formChanges).length === 0) ? false : true;

        return isHypothetical;
    }

    public getCompanyLogoByAssociation(association: any, location: string = Branding.appBrandLogoLocations.reportFooter) {
        const assocCompanyBrandKey = _.get(association, 'company.brandKey', Branding.appBrandKeys.complexSolutions);

        const path = `${assocCompanyBrandKey}.logo.${location}`;
        const result = _.get(AppImages, path, '');

        return result;
    }

    public getBrandingCssNameByAssociation(association: any) {
        const assocCompanyBrandKey = _.get(association, 'company.brandKey', Branding.appBrandKeys.complexSolutions);

        return assocCompanyBrandKey;
    }

    public registerSectionPageRef = (element: any, sectionName: string, pageIndex: number = 0) => {
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (existing) {
            if (_.get(existing, 'elementRef') !== element) {
                _.set(existing, 'elementRef', element);
            }
        }
    }

    public registerSectionPage = (sectionName: string, pageTitle: string = '', pageIndex: number = 0, includeInPageCount: boolean = true): number => {
        const eligiblePages = _.filter(this.sectionPages, (o) => _.get(o, 'includeInPageCount', true));
        const pageCount = _.size(eligiblePages);

        const pageNumber = (includeInPageCount) ? pageCount + 1 : 0;
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (!existing) {
            const page = {
                elementRef: undefined,
                includeInPageCount: includeInPageCount,
                pageKey: pageKey,
                pageNumber: pageNumber,
                pageTitle: pageTitle,
                sectionName: sectionName,
            };

            this.sectionPages.push(page);

            if (includeInPageCount) {
                this.setState({ reportPageCount: pageCount + 1 });
            }
        } else {
            return existing.pageNumber;
        }

        return pageNumber;
    }

    public registerComponentFundingSectionPageRef = (element: any, sectionName: string, pageIndex: number = 0) => {
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (existing) {
            if (_.get(existing, 'elementRef') !== element) {
                _.set(existing, 'elementRef', element);
            }
        }
    }

    public registerComponentFundingSectionPage = (sectionName: string, pageTitle: string = '', pageIndex: string = '', includeInPageCount: boolean = true): number => {
        const eligiblePages = _.filter(this.sectionPages, (o) => _.get(o, 'includeInPageCount', true));
        const pageCount = _.size(eligiblePages);

        const pageNumber = (includeInPageCount) ? pageCount + 1 : 0;
        const pageKey = `${sectionName}-${pageIndex}`;

        const existing = _.find(this.sectionPages, ['pageKey', pageKey]);
        if (!existing) {
            const page = {
                elementRef: undefined,
                includeInPageCount: includeInPageCount,
                pageKey: pageKey,
                pageNumber: pageNumber,
                pageTitle: pageTitle,
                sectionName: sectionName,
            };

            this.sectionPages.push(page);

            if (includeInPageCount) {
                this.setState({ reportPageCount: pageCount + 1 });
            }
        } else {
            return existing.pageNumber;
        }

        return pageNumber;
    }

    public getFirstPageOfSection = (secName: string): any => _(this.sectionPages).sortBy('pageNumber').find(['sectionName', secName]);

    public getPageNum = (secName: string) => _.get(this.getFirstPageOfSection(secName), 'pageNumber');

    public getPageRef = (secName: string) => _.get(this.getFirstPageOfSection(secName), 'elementRef');

    // HANDLERS
    public handlePrintPageButton(e: any) {
        window.print();
    }

    public handleViewOrderButton(e: any, orderId: any) {
        this.props.navigateTo(NavigationLinks.orderDetail(orderId));
    }

    public handleReserveStudyButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.reserveStudy(associationId));
    }

    public handleViewAssociationButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationDetail(associationId));
    }

    public handleViewInventoryButton(e: any) {
        const { associationId } = this.props;
        this.props.navigateTo(NavigationLinks.associationInventory(associationId));
    }

    public handleScrollTo = (e: any, s: string = '') => {
        if (e) { e.preventDefault(); }

        scrollToComponent(this.getPageRef(s), AssociationReportPdf.scrollToConfig);

        return false;
    }

    // RENDERERS
    public renderHeaderButtonToolbar = () => {
        let buttonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };

        const currentOrderId = Formatters.getInt(this.props, 'currentOrder.orderId');

        return (
            <div className="header-print-buttontoolbar-report">
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <Button {...buttonProps} onClick={(e) => { this.handlePrintPageButton(e); }}>
                                    <i className="glyphicon glyphicon-print" /> {this.appTextCommon('buttons.print-document')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                        <Col xs={9}>
                            <ButtonToolbar>
                                <Button {...buttonProps} onClick={e => { this.handleViewAssociationButton(e); }}>
                                    <i className="glyphicon glyphicon-eye-open" /> {this.appTextCommon('buttons.view-association')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleViewOrderButton(e, currentOrderId); }}>
                                    <i className="fa fa-shopping-cart" /> {this.appTextCommon('buttons.view-order')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleViewInventoryButton(e); }}>
                                    <i className="fa fa-cubes" /> {this.appTextCommon('buttons.view-inventory')}
                                </Button>
                                <Button {...buttonProps} onClick={e => { this.handleReserveStudyButton(e); }}>
                                    <i className="fa fa-line-chart" /> {this.appTextCommon('buttons.view-reserve-study')}
                                </Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public render() {
        const { currentOrder, currentOrderChart, associationId } = this.props;

        const association = this.extendAssociationData(this.props.association);
        const inventory: Array<any> = this.extendInventoryData(this.props.inventory, this.state.inventoryChanges, association);

        let cloneCurrentOrderChart = _.cloneDeep(currentOrderChart);
        let cloneCurrentOrder = _.cloneDeep(currentOrder);

        const isLoading = Number(Formatters.getInt(association, 'associationId')) !== Number(associationId);
        const missingOrderdata = isNullOrUndefined(currentOrder) ||
            isNullOrUndefined(currentOrder.serviceLevel) ||
            isNullOrUndefined(currentOrder.serviceLevel.report);
        const noChartData = _.isEmpty(currentOrder) ||
            _.isEmpty(currentOrderChart) ||
            _.isEmpty(association) ||
            _.isEmpty(inventory);

        let noChartExplanation = '';
        if (_.isEmpty(currentOrder)) {
            noChartExplanation = 'Current Order missing or invalid.';
        } else if (_.isEmpty(currentOrderChart)) {
            noChartExplanation = 'Current Order Chart missing or invalid.';
        } else if (_.isEmpty(association)) {
            noChartExplanation = 'Association missing or invalid.';
        } else if (_.isEmpty(inventory)) {
            noChartExplanation = 'Inventory missing or invalid.';
        }

        let page = (<div />);
        let brandingCssName = '';

        let hasComponentsWithNegativeRUL = (_.find(inventory, (o) => Formatters.getInt(o, 'remainingUsefulLife_sort') < 0)) ? true : false;

        if (!isLoading) {
            if (noChartData) {
                page = (
                    <div>
                        <Row>
                            <Col xs={4}>
                                <Alert bsStyle="info">
                                    <h4>{this.appText('chart-data-missing')}</h4>
                                    <p>{this.appText('chart-data-missing-body')}</p>
                                    <p>{noChartExplanation}</p>
                                </Alert>
                            </Col>
                        </Row>
                    </div>
                );
            } else if (hasComponentsWithNegativeRUL) {
                page = (
                    <div className="association-inventory-alert-component-negative-rul">
                        <Alert bsStyle="warning">
                            <h4><i className="fa fa-exclamation-triangle" /> {this.appText('alerts.warn-component-negative-rul.title')}</h4>
                            <p>{this.appText('alerts.warn-component-negative-rul.body')}</p>
                        </Alert>
                    </div>
                );
            } else if (missingOrderdata) {
                page = (
                    <div>
                        <Row>
                            <Col xs={4}>
                                <Alert bsStyle="info">
                                    <h4>{this.appText('order-data-missing')}</h4>
                                    <p>{this.appText('order-data-missing-body')}</p>
                                </Alert>
                            </Col>
                        </Row>
                    </div>
                );
            } else {
                let currentOrderUsers = this.extendCurrentOrderUsers(cloneCurrentOrder, this.props.userResumes);

                const isHypothetical = this.getIsHypothetical();
                const formData = this.getFormData(cloneCurrentOrderChart, cloneCurrentOrder);

                const mergedCurrentOrderChart = _.merge({}, cloneCurrentOrderChart, formData);

                const orderChartBeginYear = _.get(mergedCurrentOrderChart, 'beginYear');
                const assocFiscalYearEnd = _.get(association, 'details.fiscalYearEnd');
                const perFirstYear = Number(_.get(mergedCurrentOrderChart, 'perFirstYear', 12));
                const fyeDateStr = `${orderChartBeginYear}/${assocFiscalYearEnd}`;
                const dateEndAssocFYE = new Date(fyeDateStr);
                const reportPeriodEndDateMoment = moment(dateEndAssocFYE).toObject();
                const reportPeriodEndDate = moment(reportPeriodEndDateMoment); // .subtract(1, 'day').toObject();
                const reportPeriodStartDate = moment(reportPeriodEndDateMoment).subtract(perFirstYear, 'month').add(1, 'day').toObject();

                const calculationResult = ReportLogic.computeFundingCalculations(mergedCurrentOrderChart, cloneCurrentOrder, inventory, ReportCommon.CalculateYears);

                const calculations = AppUtilities.getArray(calculationResult, 'recordSet');
                const invMap = AppUtilities.getArray(calculationResult, 'invMap');

                const percentFunded = ReportLogic.getPercentFunded(calculations, isHypothetical);

                // REPORT PAGES CONFIGURATION
                const pageConfiguration = {
                    maxTableRowsPerPage: 24, // Causes page breaks if a table exceeds this # rows
                    maxTableRowsPerFirstPage: 18, // Causes page breaks if a table exceeds this # rows
                    maxColumnsPerTable: 3, // Used on yearly cash flow, etc
                    charactersPerLine: 110,
                    kendoCharts: {
                        renderAs: 'svg',
                    },
                    charts: {
                        xAxis: {
                            title: {
                                x: -40,
                                y: 20,
                            },
                        },
                        legend: {
                            verticalAlign: 'bottom',
                            align: 'center',
                            x: 0,
                            y: 0,
                        },
                        chart: {
                            height: 600
                        }
                    }
                };

                // YEARLY SUMMARY DATA
                const yearlySummaryData = ReportCharts.getYearlySummaryGridData((e) => this.appText(e), calculations, formData.beginYear, formData.calculateYears, isHypothetical);

                // COMPONENT EVALUATIONS
                const isComponentEvaluationHidden = (_.get(mergedCurrentOrderChart, 'hideComponentEvaluation', false)) ? true : false;
                const hideComponentEvaluation = (isLevel3Report(currentOrder) || isComponentEvaluationHidden) ? true : false;
                const showComponentEvaluation = (hideComponentEvaluation) ? false : true;
                const showYearlySummaryCurrent = Formatters.getBool(mergedCurrentOrderChart, 'showYearlySummaryCurrent');
                const showYearlySummaryMinimum = Formatters.getBool(mergedCurrentOrderChart, 'showYearlySummaryMinimum');
                const showYearlySummaryRecommended = Formatters.getBool(mergedCurrentOrderChart, 'showYearlySummaryRecommended');

                // REPORT FOOTER
                const showReportPageFooter = !((_.get(mergedCurrentOrderChart, 'hideFooter', false)) ? true : false);

                // Limit to 30 years
                const yearlySummaryDataCorrected = _.take(yearlySummaryData, formData.calculateYears);
                const calculationsCorrected = _.take(calculations, formData.calculateYears);

                // FOOTER DATA
                const reportData = {
                    association: association,
                    showComponentEvaluation: showComponentEvaluation,
                    showFooterFull: showReportPageFooter,
                    branding: {
                        logoCoverPage: this.getCompanyLogoByAssociation(association, Branding.appBrandLogoLocations.reportCoverPage),
                        logoFooter: this.getCompanyLogoByAssociation(association, Branding.appBrandLogoLocations.reportFooter),
                    },
                    calculations: calculationsCorrected, // calculations,
                    currentOrder: currentOrder,
                    currentOrderChart: mergedCurrentOrderChart, // currentOrderChart,
                    mergedCurrentOrderChart: mergedCurrentOrderChart,
                    specialAssessmentRows: normalizeSpecialAssessments(mergedCurrentOrderChart, formData.beginYear, ReportCommon.CalculateYears),
                    currentOrderUsers: currentOrderUsers,
                    formData: formData,
                    inventory: invMap,
                    isHypothetical: isHypothetical,
                    percentFunded: percentFunded,
                    yearlySummaryData: yearlySummaryDataCorrected, // yearlySummaryData,
                    reportPeriodStartDate: reportPeriodStartDate,
                    reportPeriodEndDate: reportPeriodEndDate,
                    showYearlySummaryCurrent: showYearlySummaryCurrent,
                    showYearlySummaryMinimum: showYearlySummaryMinimum,
                    showYearlySummaryRecommended: showYearlySummaryRecommended
                };

                // Page numbers are assigned in first-render order and never recomputed, so rebuild the registry whenever the schedule's page count changes.
                const scheduleRowCount = getSpecialAssessmentPresentation(reportData.specialAssessmentRows).showInline ? 0 : _.size(reportData.specialAssessmentRows);
                const scheduleLayoutKey = Math.ceil(scheduleRowCount / SPECIAL_ASSESSMENT_SCHEDULE_ROWS_PER_PAGE);
                if (this.scheduleLayoutKey !== scheduleLayoutKey) {
                    this.scheduleLayoutKey = scheduleLayoutKey;
                    this.sectionPages = [];
                }

                brandingCssName = this.getBrandingCssNameByAssociation(association);

                const pageCover = (
                    <ReportPageCover
                        analysisMethod={this.appText('cash-flow')}
                        fundingGoal={this.appText('full-funding')}
                        reportData={reportData}
                        appText={(e) => this.appText(e)}
                    />
                );

                const reportPagesProps = {
                    appText: (e) => this.appText(e),
                    pageConfiguration: pageConfiguration,
                    pageCount: this.state.reportPageCount,
                    refFunc: (e, s, i) => this.registerSectionPageRef(e, s, i),
                    registerFunc: (s, p, i, b) => this.registerSectionPage(s, p, i, b),
                    reportData: reportData,
                    scrollToFunc: (e, s) => this.handleScrollTo(e, s),
                    sectionPages: this.sectionPages,
                };

                const executiveSummaryTitle = `${this.appText('page.executive-summary')} - ${Formatters.getStr(association, 'name')} - ID # ${Formatters.getStr(association, 'associationId')}`;

                const reportPages = (
                    <div>
                        <ReportPageExecutiveSummary
                            className="pdf-executive-summary-page"
                            key={ReportComponents.getRowKey(`section-1`, '1', '1')}
                            pageTitle={executiveSummaryTitle}
                            {...reportPagesProps}
                        />

                        <ReportPageIntroduction
                            className="pdf-introduction-page"
                            key={ReportComponents.getRowKey(`section-2`, '2', '2')}
                            pageTitle={this.appText('page.introduction')}
                            {...reportPagesProps}
                        />

                        <ReportPageFaq
                            className="pdf-faq-page"
                            key={ReportComponents.getRowKey(`section-3`, '3', '3')}
                            pageTitle={this.appText('page.general-information')}
                            {...reportPagesProps}
                        />

                        <ReportPageFundingSummary
                            className="pdf-funding-summary-page"
                            key={ReportComponents.getRowKey(`section-4`, '4', '4')}
                            pageTitle={this.appText('page.funding-summary')}
                            {...reportPagesProps}
                        />

                        {(!getSpecialAssessmentPresentation(reportData.specialAssessmentRows).showInline) ? (
                            <ReportPageSpecialAssessments
                                className="pdf-page-special-assessments"
                                key={ReportComponents.getRowKey(`section-4-sa`, '4-sa', '4-sa')}
                                maxTableRowsPerPage={SPECIAL_ASSESSMENT_SCHEDULE_ROWS_PER_PAGE}
                                pageTitle={this.appText('page.special-assessments')}
                                {...reportPagesProps}
                            />
                        ) : (<div />)}

                        <ReportPagePercentFundedGraphKendo
                            className="pdf-page-percent-funded-graph"
                            key={ReportComponents.getRowKey(`section-5`, '5', '5')}
                            pageTitle={this.appText('page.percent-funded-graph')}
                            {...reportPagesProps}
                        />
                    </div>
                );

                const firstCompFunding = (
                    <div>
                        <ReportPageComponentFunding
                            className="pdf-page-component-funding"
                            key={ReportComponents.getRowKey(`section-6`, '6', '6')}
                            {...reportPagesProps}
                            maxTableRowsPerFirstPage={33}
                            maxTableRowsPerPage={33}
                            mrcValue={currentOrderChart.recommendedMRCVal}
                            pageTitle={this.appText('page.component-funding-information')}
                            firstPass={true}
                            refFunc={(e, s, i) => this.registerComponentFundingSectionPageRef(e, s, i)}
                            registerFunc={(s, p, i, b) => this.registerComponentFundingSectionPage(s, p, i, b)}
                        />
                    </div>
                );

                const restOfReport = (
                    <div>
                        <ReportPageYearlySummary
                            className="pdf-page-yearly-summary"
                            key={ReportComponents.getRowKey(`section-7`, '7', '7')}
                            maxTableRowsPerPage={31}
                            pageTitle={(showYearlySummaryCurrent || showYearlySummaryMinimum) ? this.appText('page.yearly-summary-recommended-funding') : this.appText('page.yearly-summary')}
                            // yearlySummaryType={ReportComponents.YearlySummaryTypes.hypoValues}
                            yearlySummaryType={ReportComponents.YearlySummaryTypes.recommendedValues}
                            {...reportPagesProps}
                        />

                        {(showYearlySummaryCurrent) ? (
                            <ReportPageYearlySummary
                                className="pdf-page-yearly-summary"
                                key={ReportComponents.getRowKey(`section-7-ys-current`, '7-ys-current', '7-ys-current')}
                                maxTableRowsPerPage={31}
                                pageTitle={this.appText('page.yearly-summary-current')}
                                yearlySummaryType={ReportComponents.YearlySummaryTypes.currentValues}
                                {...reportPagesProps}
                            />
                        ) : (<div />)
                        }

                        {(showYearlySummaryMinimum) ? (
                            <ReportPageYearlySummary
                                className="pdf-page-yearly-summary"
                                key={ReportComponents.getRowKey(`section-7-ys-minimum`, '7-ys-minimum', '7-ys-minimum')}
                                maxTableRowsPerPage={31}
                                pageTitle={this.appText('page.yearly-summary-minimum')}
                                yearlySummaryType={ReportComponents.YearlySummaryTypes.minimumValues}
                                {...reportPagesProps}
                            />
                        ) : (<div />)
                        }

                        <ReportPageReserveContributionsGraphKendo
                            className="pdf-page-reserve-contributions-graph"
                            key={ReportComponents.getRowKey(`section-8`, '8', '8')}
                            pageTitle={this.appText('page.reserve-contributions-graph')}
                            {...reportPagesProps}
                        />

                        <ReportPageSignificantComponents
                            className="pdf-page-significant-components"
                            key={ReportComponents.getRowKey(`section-9`, '9', '9')}
                            maxTableRowsPerFirstPage={32}
                            maxTableRowsPerPage={32}
                            pageTitle={this.appText('page.significant-components')}
                            {...reportPagesProps}
                        />

                        <ReportPageSignificantComponentsGraphKendo
                            className="pdf-page-significant-components-graph"
                            key={ReportComponents.getRowKey(`section-10`, '10', '10')}
                            pageTitle={this.appText('page.significant-components-graph')}
                            topComponentCount={4}
                            {...reportPagesProps}
                        />

                        <ReportPageYearlyCashFlow
                            className="pdf-page-yearly-cash-flow"
                            key={ReportComponents.getRowKey(`section-11`, '11', '11')}
                            maxColumnsPerTable={5}
                            maxTableRowsPerPage={5}
                            pageTitle={this.appText('page.yearly-cash-flow')}
                            {...reportPagesProps}
                        />

                        <ReportPageYearlyReserveExpendituresGraphKendo
                            className="pdf-page-yearly-reserve-expenditures-graph"
                            key={ReportComponents.getRowKey(`section-12`, '12', '12')}
                            pageTitle={this.appText('page.yearly-reserve-expenditures-graph')}
                            {...reportPagesProps}
                        />

                        <ReportPageProjectedReserveExpenditures
                            className="pdf-page-projected-reserve-expenditures"
                            key={ReportComponents.getRowKey(`section-13`, '13', '13')}
                            // maxTableRowsPerPage={33}
                            maxTableRowsPerFirstPage={36} // 20191003 38
                            maxTableRowsPerPage={36} // 20191003 38
                            pageTitle={this.appText('page.projected-reserve-expenditures-by-year')}
                            {...reportPagesProps}
                        />

                        {(showComponentEvaluation) ? (
                            <ReportPageComponentEvaluation
                                className="pdf-page-component-evaluation"
                                estCharactersPerLine={115}
                                key={ReportComponents.getRowKey(`section-14`, '14', '14')}
                                maxTableRowsPerFirstPage={20}
                                maxTableRowsPerPage={15}
                                pageTitle={this.appText('page.component-evaluation')}
                                {...reportPagesProps}
                            />
                        ) : (<div />)
                        }

                        <ReportPageGlossary
                            className="pdf-page-glossary"
                            estCharactersPerLine={115}
                            key={ReportComponents.getRowKey(`section-15`, '15', '15')}
                            maxTableRowsPerFirstPage={20}
                            maxTableRowsPerPage={15}
                            pageTitle={this.appText('page.glossary')}
                            {...reportPagesProps}
                        />
                    </div>
                );

                const secondCompFunding = (
                    <div>
                        <ReportPageComponentFunding
                            className="pdf-page-component-funding"
                            key={ReportComponents.getRowKey(`section-6`, '6', '6')}
                            {...reportPagesProps}
                            maxTableRowsPerFirstPage={33}
                            maxTableRowsPerPage={33}
                            mrcValue={currentOrderChart.recommendedMRCVal}
                            pageTitle={this.appText('page.component-funding-information')}
                            firstPass={false}
                            refFunc={(e, s, i) => this.registerComponentFundingSectionPageRef(e, s, i)}
                            registerFunc={(s, p, i, b) => this.registerComponentFundingSectionPage(s, p, i, b)}
                        />
                    </div>
                );

                const pageTOC = (
                    <ReportPageTOC
                        className="pdf-table-of-contents-page"
                        pageConfiguration={pageConfiguration}
                        pageCount={this.state.reportPageCount}
                        pageTitle={this.appText('page.table-of-contents')}
                        refFunc={(e, s, i) => this.registerSectionPageRef(e, s, i)}
                        registerFunc={(s, p, i, b) => this.registerSectionPage(s, p, i, b)}
                        reportData={reportData}
                        scrollToFunc={(e, s) => this.handleScrollTo(e, s)}
                        sectionPages={this.sectionPages}
                    />
                );

                page = (
                    <div>
                        {this.renderHeaderButtonToolbar()}
                        <Row className="pdf-report-container">
                            <Col xs={12}>
                                <div className="pdf-report pdf-reserve-study-report center-block">
                                    <div className="pdf-reserve-study-report-container">
                                        {pageCover}
                                        {pageTOC}
                                        {reportPages}
                                        {isComponentEvaluationHidden ? firstCompFunding : secondCompFunding}
                                        {restOfReport}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            }
        }

        const { ...rest } = this.props;
        return (
            <div className={brandingCssName}>
                <AppComp.RenderPageWithLayout branding={this.props.branding} pageName="" {...rest}>
                    {page}
                </AppComp.RenderPageWithLayout>
            </div>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getReportForAssociationUrl(props.match.params.associationId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getReportForAssociationRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    const assocId = props.match.params.associationId;
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        association: state.entities.reportAssociation,
        associationId: assocId,

        currentOrder: state.entities.reportAssociationCurrentOrder,
        currentOrderChart: state.entities.reportAssociationCurrentOrderChart,
        inventory: state.entities.reportAssociationInventory,
        scenarios: state.entities.reportAssociationScenarios,
        userResumes: state.entities.reportAssociationUserResumes,
    });
};

const mapDispatchToProps = {
    navigateTo: (path) => push(path)
};

const AssociationReportPdfContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AssociationReportPdf);

export default withRouter(AssociationReportPdfContainer);



// WEBPACK FOOTER //
// ./src/report/pdf/pdf.tsx